/*
 * Copyright 2026 Sony Corporation
 */

import { GizmoManager } from '@babylonjs/core/Gizmos/gizmoManager';
import { PointerEventTypes } from '@babylonjs/core/Events/pointerEvents';
import type { Node } from '@babylonjs/core/node';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';

import { createCameraRig, spawnTransform, type CameraRig } from './camera-rig';
import { DEFAULT_OPTICS, type ResolvedOptics } from './camera-optics';
import { createThrottle } from './throttle';
import type { SceneEditor } from './scene-editor';
import type { Transform } from './types';

const RIG_PREFIX = 'rig:';

/** Walks up from a picked mesh to the rig it belongs to, if any. */
const deviceIdOf = (picked: Node | undefined): string | undefined => {
  for (let node = picked; node; node = node.parent ?? undefined) {
    if (node.name.startsWith(RIG_PREFIX)) {
      return node.name.slice(RIG_PREFIX.length).split(':')[0];
    }
  }
  return undefined;
};

export interface RigDefinition {
  deviceId: string;
  /** Absent for a device that has never been placed. */
  transform?: Transform;
  /** Proposed by the assistant and not confirmed: drawn, but not a device yet. */
  pending?: boolean;
  /** What the frustum is drawn with; the generic 50 mm when absent. */
  optics?: ResolvedOptics;
}

export interface RigManager {
  /** Creates, updates and removes rigs to match the given devices. */
  sync: (devices: readonly RigDefinition[]) => void;
  select: (deviceId: string | undefined) => void;
  /** The node a preview device parents itself to, if that rig exists yet. */
  rootOf: (deviceId: string | undefined) => TransformNode | undefined;
  /** Re-places and re-scales every rig after a scene switch. */
  refit: () => void;
  dispose: () => void;
}

export interface RigManagerOptions {
  editor: SceneEditor;
  /** Called at most once per interval while dragging, and once on release. */
  onTransformChanged: (deviceId: string, transform: Transform) => void;
  /** A click on a rig in the viewport; undefined when the click missed. */
  onSelectRequested?: (deviceId: string | undefined) => void;
  throttleMs?: number;
}

/** 10 Hz while dragging, per devplan 3.9. */
const DEFAULT_THROTTLE_MS = 100;

export const createRigManager = ({
  editor,
  onTransformChanged,
  onSelectRequested,
  throttleMs = DEFAULT_THROTTLE_MS,
}: RigManagerOptions): RigManager => {
  const rigs = new Map<string, CameraRig>();
  let selectedId: string | undefined;
  /** Set only between a gizmo grab and its release. */
  let draggingId: string | undefined;

  const throttle = createThrottle<Transform>({
    intervalMs: throttleMs,
    send: (deviceId, transform) => onTransformChanged(deviceId, transform),
  });

  // One manager for the editor's lifetime: a gizmo per selection would leak its
  // meshes, and it has to outlive every scene switch (scene-switch.md §6).
  const gizmos = new GizmoManager(editor.scene);
  gizmos.positionGizmoEnabled = true;
  gizmos.rotationGizmoEnabled = true;
  gizmos.usePointerToAttachGizmos = false;

  const sceneRadius = () => editor.activeBounds().radius;

  const selectedRig = (): CameraRig | undefined => (selectedId ? rigs.get(selectedId) : undefined);

  const publish = (final: boolean) => {
    const rig = selectedRig();
    if (!rig) {
      return;
    }
    draggingId = final ? undefined : rig.deviceId;
    throttle.push(rig.deviceId, rig.readTransform());
    if (final) {
      throttle.flush(rig.deviceId);
    }
  };

  // Drag fires every frame; release is the one update that must not be dropped.
  for (const gizmo of [
    gizmos.gizmos.positionGizmo?.xGizmo,
    gizmos.gizmos.positionGizmo?.yGizmo,
    gizmos.gizmos.positionGizmo?.zGizmo,
    gizmos.gizmos.rotationGizmo?.xGizmo,
    gizmos.gizmos.rotationGizmo?.yGizmo,
    gizmos.gizmos.rotationGizmo?.zGizmo,
  ]) {
    gizmo?.dragBehavior.onDragObservable.add(() => publish(false));
    gizmo?.dragBehavior.onDragEndObservable.add(() => publish(true));
  }

  const select = (deviceId: string | undefined) => {
    selectedId = deviceId;

    for (const rig of rigs.values()) {
      rig.setSelected(rig.deviceId === deviceId);
    }

    const rig = selectedRig();
    gizmos.attachToNode(rig ? rig.root : null);
  };

  // Clicking the rig itself is what anyone tries first, so the viewport reports
  // a pick and the caller turns it into a selection.
  const pickObserver = editor.scene.onPointerObservable.add((info) => {
    if (info.type !== PointerEventTypes.POINTERPICK || !onSelectRequested) {
      return;
    }
    onSelectRequested(deviceIdOf(info.pickInfo?.pickedMesh ?? undefined));
  });

  return {
    sync: (devices) => {
      const wanted = new Set(devices.map((device) => device.deviceId));

      for (const [deviceId, rig] of [...rigs]) {
        if (!wanted.has(deviceId)) {
          if (selectedId === deviceId) {
            select(undefined);
          }
          rig.dispose();
          rigs.delete(deviceId);
        }
      }

      const bounds = editor.activeBounds();
      devices.forEach((device, index) => {
        const transform = device.transform ?? spawnTransform(index, bounds.center, bounds.radius);
        const existing = rigs.get(device.deviceId);

        if (existing) {
          existing.setOptics(device.optics ?? DEFAULT_OPTICS);
          // Only the hand on the gizmo is refused: a selected rig still has to
          // follow someone else moving the same device in another tab.
          if (device.transform && device.deviceId !== draggingId) {
            existing.setTransform(device.transform);
          }
          return;
        }

        rigs.set(
          device.deviceId,
          createCameraRig({
            scene: editor.scene,
            deviceId: device.deviceId,
            transform,
            sceneRadius: bounds.radius,
            pending: device.pending,
            optics: device.optics,
          })
        );
      });

      select(selectedId && wanted.has(selectedId) ? selectedId : undefined);
    },

    select,

    rootOf: (deviceId) => (deviceId ? rigs.get(deviceId)?.root : undefined),

    refit: () => {
      const radius = sceneRadius();
      for (const rig of rigs.values()) {
        rig.setSceneRadius(radius);
      }
      select(selectedId);
    },

    dispose: () => {
      throttle.cancel();
      editor.scene.onPointerObservable.remove(pickObserver);
      gizmos.attachToNode(null);
      gizmos.dispose();
      for (const rig of rigs.values()) {
        rig.dispose();
      }
      rigs.clear();
    },
  };
};
